/**
 * Monthly payment modal and Newcoast prequalification runtime.
 *
 * Keeps modal routing, lazy third-party loading, application URL state,
 * analytics, and third-party alert alignment behind one lifecycle owner.
 */

(() => {
  const RUNTIME_KEY = '__MarineMaxMonthlyPaymentModal';
  const CALCULATOR_MODAL_ID = 'monthly-payment-calculator-modal';
  const BOATZON_MODAL_ID = 'boatzon-prequalify-modal';
  const NEWCOAST_COMPONENT_ID = 'newcoast-prequalify-component';
  const NEWCOAST_COMPONENT_NAME = 'newcoast-prequalify';
  const NEWCOAST_SCRIPT_SELECTOR = 'script[data-mm-newcoast-prequalify]';
  const NEWCOAST_SCRIPT_URL =
    'https://components.boatzon.com/newcoast-prequalify.umd.cjs';
  const CALCULATOR_TRIGGER_SELECTOR =
    'a[href="#monthly-payment-calculator"], a[href="#monthly-payment-calculator-modal"]';
  const BOATZON_TRIGGER_SELECTOR = 'a[href="#boatzon-prequalify-modal"]';
  const BOATZON_ALERT_SELECTOR =
    '[role="alert"], [aria-live="assertive"], [class*="notification"], [class*="Toast"], [class*="toast"]';
  const SHADOW_STYLE_ID = 'mm-boatzon-shadow-align';
  const ALIGN_DELAYS = [0, 200, 800, 2000];

  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime?.initialize) {
    existingRuntime.initialize();
    return;
  }

  let newcoastLoadPromise = null;

  function getClosestTrigger(event, selector) {
    const target = event?.target;
    return typeof target?.closest === 'function' ? target.closest(selector) : null;
  }

  function pushAnalyticsEvent(payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (_) {
      // Analytics must never block the customer flow.
    }
  }

  function waitForNewcoastDefinition(resolve, reject) {
    if (window.customElements?.whenDefined) {
      window.customElements
        .whenDefined(NEWCOAST_COMPONENT_NAME)
        .then(resolve, reject);
      return;
    }

    resolve();
  }

  function ensureNewcoastLoaded() {
    if (window.customElements?.get(NEWCOAST_COMPONENT_NAME)) {
      return Promise.resolve();
    }
    if (newcoastLoadPromise) return newcoastLoadPromise;

    newcoastLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(NEWCOAST_SCRIPT_SELECTOR);
      const script = existingScript || document.createElement('script');
      const resolveWhenDefined = () => waitForNewcoastDefinition(resolve, reject);

      if (existingScript) {
        // The script may already have loaded before this section was re-rendered.
        // Waiting on the custom element directly avoids attaching a late load
        // listener that can never fire.
        resolveWhenDefined();
        script.addEventListener('error', reject, { once: true });
        return;
      }

      script.src = NEWCOAST_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-mm-newcoast-prequalify', '');
      script.addEventListener('load', resolveWhenDefined, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      newcoastLoadPromise = null;
      throw error;
    });

    return newcoastLoadPromise;
  }

  class BoatzonPrequalification {
    constructor({ calculatorModal, modal, component, prequalifiedButton }) {
      this.calculatorModal = calculatorModal;
      this.modal = modal;
      this.component = component;
      this.prequalifiedButton = prequalifiedButton;
      this.eventAbortController = new AbortController();
      this.observers = new Set();
      this.observedTargets = new Set();
      this.alignTimeouts = new Set();
      this.animationFrames = new Set();
      this.resizeTimeout = null;
      this.isDestroyed = false;

      this.handlePrequalifiedClick = this.handlePrequalifiedClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.pinAlerts = this.pinAlerts.bind(this);
      this.scheduleResizePin = this.scheduleResizePin.bind(this);

      this.initialize();
    }

    initialize() {
      const listenerOptions = { signal: this.eventAbortController.signal };
      const applicationId = new URLSearchParams(window.location.search).get('id');

      if (applicationId) {
        this.component.setAttribute('app-id', applicationId);
      }

      this.component.addEventListener('submit', this.handleSubmit, listenerOptions);
      this.prequalifiedButton?.addEventListener(
        'click',
        this.handlePrequalifiedClick,
        listenerOptions,
      );
      window.addEventListener('resize', this.scheduleResizePin, listenerOptions);
      window.addEventListener('scroll', this.scheduleResizePin, {
        capture: true,
        signal: this.eventAbortController.signal,
      });

      this.observe(this.modal);
      this.attachShadowFix();

      if (window.customElements?.whenDefined) {
        window.customElements
          .whenDefined(NEWCOAST_COMPONENT_NAME)
          .then(() => {
            if (!this.isDestroyed) this.scheduleShadowAlignFix();
          })
          .catch(() => {});
      }
    }

    handleSubmit(event) {
      const detail = event.detail?.[0];
      const applicationId = detail?.appId || detail?.id;

      if (!applicationId) return;
      if (detail.eventName && detail.eventName !== 'pre-qualified') return;

      const url = new URL(window.location.href);
      url.searchParams.set('id', applicationId);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    handlePrequalifiedClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.loadAndOpen(this.prequalifiedButton);
      pushAnalyticsEvent({
        event: 'calculator_prequalified',
        calculator_type: 'loan',
        page_location: window.location.href,
      });
    }

    loadAndOpen(trigger) {
      trigger?.setAttribute('aria-busy', 'true');

      return ensureNewcoastLoaded()
        .then(() => {
          if (this.isDestroyed) return;
          this.open(trigger);
        })
        .catch((error) => {
          console.error('[Newcoast] Unable to load prequalification form', error);
        })
        .finally(() => {
          trigger?.removeAttribute('aria-busy');
        });
    }

    open(trigger) {
      if (typeof this.calculatorModal?.hide === 'function') {
        this.calculatorModal.hide();
      }
      if (typeof this.modal.show === 'function') {
        this.modal.show(trigger);
      }
      this.scheduleShadowAlignFix();
    }

    injectShadowAlignStyles(shadowRoot) {
      if (!shadowRoot || shadowRoot.getElementById(SHADOW_STYLE_ID)) return;

      const style = document.createElement('style');
      style.id = SHADOW_STYLE_ID;
      style.textContent =
        ':host{display:block;min-height:0;overflow:visible!important}' +
        '[role="alert"],[aria-live="assertive"],[class*="notification"],[class*="toast"],[class*="Toast"]{' +
        'box-sizing:border-box!important;white-space:normal!important;' +
        'word-break:normal!important;overflow-wrap:anywhere!important}';
      shadowRoot.appendChild(style);
    }

    resolveAlertContainer(node) {
      if (!node) return null;

      let element = node;
      let hops = 0;
      while (element && hops < 4) {
        const rect = element.getBoundingClientRect?.();
        const hasText = Boolean(element.textContent?.trim().length > 8);
        if (rect && rect.width >= 140 && rect.height >= 24 && hasText) {
          return element;
        }
        element = element.parentElement;
        hops += 1;
      }

      return node;
    }

    getAlertNodes(root) {
      if (!root?.querySelectorAll) return [];

      const containers = Array.from(root.querySelectorAll(BOATZON_ALERT_SELECTOR))
        .map((node) => this.resolveAlertContainer(node))
        .filter((node) => Boolean(node?.textContent?.trim()));

      return containers.filter(
        (node, index) =>
          node?.contains &&
          !containers.some(
            (other, otherIndex) =>
              otherIndex !== index && other?.contains?.(node),
          ),
      );
    }

    pinAlertToModalCorner(alertElement) {
      const modalInner = this.modal.querySelector('.drawer__inner');
      if (!alertElement || !modalInner) return;

      const rect = modalInner.getBoundingClientRect();
      const padding = 12;
      const styles = {
        position: 'fixed',
        top: `${Math.round(rect.top + padding)}px`,
        right: `${Math.round(window.innerWidth - rect.right + padding)}px`,
        left: 'auto',
        bottom: 'auto',
        transform: 'none',
        margin: '0',
        'z-index': '10050',
        width: 'auto',
        'min-width': 'auto',
        'max-width': 'min(100vw - 2.5rem, 32rem)',
      };

      Object.entries(styles).forEach(([property, value]) => {
        alertElement.style.setProperty(property, value, 'important');
      });
    }

    pinAlerts() {
      if (this.isDestroyed) return;

      const roots = [this.component.shadowRoot, this.modal].filter(Boolean);
      if (this.component.shadowRoot) {
        this.injectShadowAlignStyles(this.component.shadowRoot);
      }
      roots.forEach((root) => {
        this.getAlertNodes(root).forEach((node) =>
          this.pinAlertToModalCorner(node),
        );
      });

      const frameId = window.requestAnimationFrame(() => {
        this.animationFrames.delete(frameId);
        if (this.isDestroyed) return;

        [this.component.shadowRoot, this.modal]
          .filter(Boolean)
          .forEach((root) => {
            this.getAlertNodes(root).forEach((node) =>
              this.pinAlertToModalCorner(node),
            );
          });
      });
      this.animationFrames.add(frameId);
    }

    scheduleResizePin() {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        this.resizeTimeout = null;
        this.pinAlerts();
      }, 80);
    }

    observe(target) {
      if (
        !target ||
        this.observedTargets.has(target) ||
        typeof MutationObserver !== 'function'
      ) {
        return;
      }

      try {
        const observer = new MutationObserver(this.pinAlerts);
        observer.observe(target, { childList: true, subtree: true });
        this.observers.add(observer);
        this.observedTargets.add(target);
      } catch (_) {
        // Third-party Shadow DOM may temporarily reject observation.
      }
    }

    attachShadowFix() {
      const shadowRoot = this.component.shadowRoot;
      if (shadowRoot) {
        this.injectShadowAlignStyles(shadowRoot);
        this.observe(shadowRoot);
      }
      this.pinAlerts();
    }

    scheduleShadowAlignFix() {
      this.attachShadowFix();
      ALIGN_DELAYS.forEach((delay) => {
        const timeoutId = window.setTimeout(() => {
          this.alignTimeouts.delete(timeoutId);
          if (!this.isDestroyed) this.attachShadowFix();
        }, delay);
        this.alignTimeouts.add(timeoutId);
      });
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;

      this.eventAbortController.abort();
      this.observers.forEach((observer) => observer.disconnect());
      this.observers.clear();
      this.observedTargets.clear();
      this.alignTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      this.alignTimeouts.clear();
      this.animationFrames.forEach((frameId) =>
        window.cancelAnimationFrame?.(frameId),
      );
      this.animationFrames.clear();
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }

  let integration = null;
  const lifecycleAbortController = new AbortController();

  function getElements() {
    return {
      calculatorModal: document.getElementById(CALCULATOR_MODAL_ID),
      component: document.getElementById(NEWCOAST_COMPONENT_ID),
      modal: document.getElementById(BOATZON_MODAL_ID),
      prequalifiedButton: document.querySelector('[data-prequalified-button]'),
    };
  }

  function initialize() {
    const elements = getElements();
    if (!elements.modal || !elements.component) {
      integration?.destroy();
      integration = null;
      return null;
    }

    if (
      integration &&
      !integration.isDestroyed &&
      integration.modal === elements.modal &&
      integration.component === elements.component
    ) {
      return integration;
    }

    integration?.destroy();
    integration = new BoatzonPrequalification(elements);
    return integration;
  }

  function destroy(root) {
    if (!integration) return;
    if (root?.contains && !root.contains(integration.modal)) return;

    integration.destroy();
    integration = null;
  }

  function handleDocumentClick(event) {
    const calculatorTrigger = getClosestTrigger(
      event,
      CALCULATOR_TRIGGER_SELECTOR,
    );
    if (calculatorTrigger) {
      event.preventDefault();
      const calculatorModal = document.getElementById(CALCULATOR_MODAL_ID);
      if (!calculatorModal) return;

      if (typeof calculatorModal.show === 'function') {
        calculatorModal.show(calculatorTrigger);
      } else {
        calculatorModal.removeAttribute('hidden');
      }
      return;
    }

    const boatzonTrigger = getClosestTrigger(event, BOATZON_TRIGGER_SELECTOR);
    if (!boatzonTrigger) return;

    const activeIntegration = initialize();
    if (!activeIntegration) return;

    event.preventDefault();
    activeIntegration.loadAndOpen(boatzonTrigger);
    pushAnalyticsEvent({
      event: 'financing_apply_loan_open',
      page_location: window.location.href,
    });
  }

  document.addEventListener('click', handleDocumentClick, {
    signal: lifecycleAbortController.signal,
  });
  document.addEventListener(
    'shopify:section:load',
    () => initialize(),
    { signal: lifecycleAbortController.signal },
  );
  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target),
    { signal: lifecycleAbortController.signal },
  );

  const runtime = Object.freeze({
    BoatzonPrequalification,
    destroy,
    ensureNewcoastLoaded,
    initialize,
  });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: true,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), {
      once: true,
      signal: lifecycleAbortController.signal,
    });
  } else {
    initialize();
  }
})();
